import { HttpError } from '@littlebird/auth-client';
import { type ChangeEvent, useRef, useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { type BaseModalFCProps } from '@/components/modals/modalsManager';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { subscriptionApis } from '@/features/subscription/subscription.query';
import { userStore } from '@/stores/user.store';
import { formatFileSize } from '@/utils/files';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const YEAR_RANGE = 10;

const ALLOWED_STUDENT_ID_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'application/pdf',
];
const MAX_STUDENT_ID_SIZE = 10 * 1024 * 1024;

export const StudentDiscountRequestModal = ({
  open,
  onOpenChange,
}: BaseModalFCProps) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const years = Array.from(
    { length: YEAR_RANGE + 1 },
    (_, i) => currentYear + i
  );

  const [university, setUniversity] = useState('');
  const [graduationMonth, setGraduationMonth] = useState<number | null>(null);
  const [graduationYear, setGraduationYear] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const isValid =
    university.trim().length > 0 &&
    graduationMonth !== null &&
    graduationYear !== null &&
    (graduationYear > currentYear ||
      (graduationYear === currentYear && graduationMonth >= currentMonth)) &&
    file !== null &&
    fileError === null;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (!selected) {
      setFile(null);
      setFileError(null);
      return;
    }
    if (!ALLOWED_STUDENT_ID_TYPES.includes(selected.type)) {
      setFile(null);
      setFileError('Upload a JPG, PNG, HEIC, or PDF file.');
      return;
    }
    if (selected.size > MAX_STUDENT_ID_SIZE) {
      setFile(null);
      setFileError('File must be under 10 MB.');
      return;
    }
    setFile(selected);
    setFileError(null);
  };

  const handleSubmit = async () => {
    if (
      !isValid ||
      graduationMonth === null ||
      graduationYear === null ||
      file === null ||
      isSubmitting
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('university', university.trim());
      fd.append('graduation_month', String(graduationMonth));
      fd.append('graduation_year', String(graduationYear));
      fd.append('student_id', file);
      await backendApis.subscription.submitStudentDiscountRequest(fd);
      await Promise.all([
        userStore.refresh(),
        subscriptionApis.getPricingPlans(),
      ]);
      onOpenChange(false);
      toast.success('Student discount applied to your account.');
    } catch (error) {
      const message =
        error instanceof HttpError
          ? error.message
          : 'Failed to submit request. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClassName =
    'flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
          className="min-w-0"
        >
          <YStack className="gap-4">
            <Dialog.Header>
              <Dialog.Title className="text-lg text-foreground">
                Student Discount
              </Dialog.Title>
              <Dialog.Description>
                Fill in your details below to apply for the student discount.
              </Dialog.Description>
            </Dialog.Header>
            <YStack className="gap-2">
              <Label htmlFor="university">University</Label>
              <Input
                id="university"
                placeholder="e.g. Stanford University"
                value={university}
                onChange={(e) => setUniversity(e.target.value)}
                autoFocus
              />
            </YStack>
            <YStack className="gap-2">
              <Label htmlFor="graduation-month">
                Graduation month and year
              </Label>
              <XStack className="gap-2">
                <select
                  id="graduation-month"
                  aria-label="Graduation month"
                  className={selectClassName}
                  value={graduationMonth ?? ''}
                  onChange={(e) =>
                    setGraduationMonth(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                >
                  <option value="">Month</option>
                  {MONTHS.map((name, index) => (
                    <option key={name} value={index + 1}>
                      {name}
                    </option>
                  ))}
                </select>
                <select
                  id="graduation-year"
                  aria-label="Graduation year"
                  className={selectClassName}
                  value={graduationYear ?? ''}
                  onChange={(e) =>
                    setGraduationYear(
                      e.target.value === '' ? null : Number(e.target.value)
                    )
                  }
                >
                  <option value="">Year</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </XStack>
            </YStack>
            <YStack className="gap-2">
              <Label htmlFor="student-id">Student ID</Label>
              <span className="text-sm text-foreground-muted">
                Attach a photo or scan of your student ID (max 10 MB).
              </span>
              <input
                ref={fileInputRef}
                id="student-id"
                type="file"
                accept="image/jpeg,image/png,image/heic,application/pdf"
                className="hidden"
                onChange={handleFileChange}
              />
              <XStack className="gap-2 items-center min-w-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="shrink-0"
                >
                  {file ? 'Change file' : 'Choose file'}
                </Button>
                <span className="text-sm text-foreground-muted truncate min-w-0 flex-1">
                  {file
                    ? `${file.name} · ${formatFileSize(file.size)}`
                    : 'No file selected'}
                </span>
              </XStack>
              {fileError && (
                <span className="text-sm text-destructive">{fileError}</span>
              )}
            </YStack>
            <Dialog.Footer>
              <XStack className="gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Submit
                </Button>
              </XStack>
            </Dialog.Footer>
          </YStack>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};
